// src/components/Dashboard/UserOrders.jsx

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../Auth/AuthContext";
import BASE_URL from "../../config/config";
import DateDisplay from "../common/DateDisplay";
import "../../css/UserOrders.css";

// کامپوننت Badge برای وضعیت سفارش
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: "در انتظار پرداخت", color: "warning", icon: "⏳" },
    pending_zarinpal: { label: "در انتظار تأیید", color: "info", icon: "🔄" },
    paid: { label: "پرداخت شده", color: "success", icon: "✅" },
    completed: { label: "تکمیل شده", color: "success", icon: "🎉" },
    canceled: { label: "لغو شده", color: "danger", icon: "❌" },
    failed: { label: "ناموفق", color: "danger", icon: "⚠️" },
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <span className={`status-badge status-badge--${config.color}`}>
      {config.icon} {config.label}
    </span>
  );
};

// کامپوننت Badge برای روش پرداخت
const PaymentMethodBadge = ({ method }) => {
  const methodConfig = {
    free: { label: "رایگان", color: "success", icon: "🎁" },
    card_to_card: { label: "کارت به کارت", color: "primary", icon: "💳" },
    online: { label: "آنلاین (زرین‌پال)", color: "info", icon: "🌐" },
  };

  const config = methodConfig[method] || {
    label: method,
    color: "secondary",
    icon: "💰",
  };

  return (
    <span className={`payment-badge payment-badge--${config.color}`}>
      {config.icon} {config.label}
    </span>
  );
};

// کامپوننت کارت سفارش
// کامپوننت کارت سفارش - نسخه کامل با نمایش وضعیت‌ها
const OrderCard = ({ order }) => {
  const [showDetails, setShowDetails] = useState(false);

  // پارس description برای اطلاعات اضافی
  let descriptionData = {};
  try {
    const parsed = JSON.parse(order.description || "{}");
    descriptionData = parsed.data || parsed;
  } catch {
    descriptionData = {};
  }

  // تابع تبدیل status به فارسی
  const getStatusLabel = (status) => {
    const statusMap = {
      'pending': { label: '⏳ در انتظار پرداخت', color: '#f39c12', bg: '#fef5e7' },
      'pending_zarinpal': { label: '🔄 در حال پردازش', color: '#3498db', bg: '#ebf5fb' },
      'paid': { label: '✅ پرداخت شده', color: '#27ae60', bg: '#d4edda' },
      'completed': { label: '🎉 تکمیل شده', color: '#16a085', bg: '#d1f2eb' },
      'cancelled': { label: '❌ لغو شده', color: '#e74c3c', bg: '#fadbd8' },
      'refunded': { label: '↩️ بازگشت وجه', color: '#9b59b6', bg: '#f4ecf7' },
      'failed': { label: '⚠️ ناموفق', color: '#c0392b', bg: '#f2d7d5' }
    };
    return statusMap[status] || { label: status, color: '#95a5a6', bg: '#ecf0f1' };
  };

  // تابع تبدیل transaction_status به فارسی
  const getTransactionStatusLabel = (txStatus) => {
    const txMap = {
      'pending': '⏰ در انتظار',
      'processing': '⚙️ در حال پردازش',
      'completed': '✅ تکمیل شده',
      'success': '✅  موفق',
      'failed': '❌ ناموفق',
      'cancelled': '🚫 لغو شده',
      'refunded': '💰 بازگشت داده شده'
    };
    return txMap[txStatus] || txStatus;
  };

  // تابع تبدیل message زرین‌پال
  const getPaymentMessage = (message) => {
    if (!message) return null;
    if (message.toLowerCase() === 'paid') return '✅ پرداخت موفق';
    return message;
  };

  const statusInfo = getStatusLabel(order.status);

  return (
    <div className="order-card">
      {/* هدر کارت */}
      <div className="order-header">
        <div className="order-header-right">
          <h3 className="order-title">سفارش {order.id}</h3>
          
          {/* نمایش status اصلی */}
          <span 
            className="status-badge"
            style={{ 
              background: statusInfo.bg, 
              color: statusInfo.color,
              border: `2px solid ${statusInfo.color}`
            }}
          >
            {statusInfo.label}
          </span>

          {/* نمایش transaction_status */}
          {order.transaction_status && (
            <span className="transaction-status-badge">
              {getTransactionStatusLabel(order.transaction_status)}
            </span>
          )}
        </div>

        <div className="order-header-left">
          <span className="order-amount">
            {parseInt(order.price).toLocaleString()}تومان 
          </span>
          <span className="payment-method-badge">
            {order.payment_method === "online" ? "💳 آنلاین" : "🏦 کارت‌به‌کارت"}
          </span>
        </div>
      </div>
    {/* نام دوره */}
      {order.course_title && (
        <div className="course-info">
          <span className="course-icon">📚</span>
          <span className="course-title">{order.course_title}</span>
        </div>
      )}

      {/* دکمه نمایش جزئیات */}
      <button 
         className={`toggle-details-btn ${showDetails ? 'active' : ''}`}
        onClick={() => setShowDetails(!showDetails)}
      >
        {showDetails ? "بستن جزئیات " : "نمایش جزئیات "}
      </button>

      {showDetails && (
        <div className="order-details">
          <hr className="divider" />

          {/* بخش وضعیت‌ها */}
          <div className="details-section status-section">
            <h4 className="details-title">📊 وضعیت سفارش</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="status-label">وضعیت سفارش:</span>
                <span 
                  className="status-value"
                  style={{ color: statusInfo.color, fontWeight: 'bold' }}
                >
                  {statusInfo.label}
                </span>
              </div>

              {order.transaction_status && (
                <div className="status-item">
                  <span className="status-label">وضعیت تراکنش:</span>
                  <span className="status-value">
                    {getTransactionStatusLabel(order.transaction_status)}
                  </span>
                </div>
              )}

              {descriptionData.message && (
                <div className="status-item">
                  <span className="status-label">پیام درگاه:</span>
                  <span 
                    className={`status-value ${descriptionData.message.toLowerCase() === 'paid' ? 'success-text' : ''}`}
                  >
                    {getPaymentMessage(descriptionData.message)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* اطلاعات کارت‌به‌کارت */}
          {order.payment_method === "card_to_card" && (
            <div className="details-section">
              <h4 className="details-title">💳 اطلاعات کارت‌به‌کارت</h4>
              <div className="details-grid">
                {descriptionData.paymentDate && (
                  <p><strong>تاریخ پرداخت:</strong> {descriptionData.paymentDate}</p>
                )}
                {descriptionData.cardNumber && (
                  <p><strong>شماره کارت:</strong> {descriptionData.cardNumber}</p>
                )}
                {descriptionData.trackingNumber && (
                  <p><strong>کد پیگیری:</strong> {descriptionData.trackingNumber}</p>
                )}
                {descriptionData.note && (
                  <p><strong>یادداشت:</strong> {descriptionData.note}</p>
                )}
                {descriptionData.paymentSlip && (
                  <div className="slip-preview">
                    <strong>فیش پرداخت:</strong>
                    <img
                      src={`${descriptionData.paymentSlip}`} 
                      alt="فیش پرداخت" 
                      className="slip-image"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* اطلاعات زرین‌پال */}
          {order.payment_method === "online" && descriptionData.ref_id && (
            <div className="details-section">
              <h4 className="details-title">🌐 اطلاعات پرداخت آنلاین</h4>
              <div className="details-grid">
                <p><strong>کد پیگیری:</strong> <span className="highlight-text">{descriptionData.ref_id}</span></p>
                
                {descriptionData.card_pan && (
                  <p><strong>شماره کارت:</strong> <code>{descriptionData.card_pan}</code></p>
                )}
                
                {/* {descriptionData.fee && (
                  <p><strong>کارمزد درگاه:</strong> {parseInt(descriptionData.fee).toLocaleString()} ریال</p>
                )}

                {descriptionData.shaparak_fee && (
                  <p><strong>کارمزد شاپرک:</strong> {parseInt(descriptionData.shaparak_fee).toLocaleString()} ریال</p>
                )} */}
                
                {/* {descriptionData.code && (
                  <p><strong>کد وضعیت:</strong> {descriptionData.code}</p>
                )} */}

                {/* {descriptionData.fee_type && (
                  <p><strong>نوع کارمزد:</strong> {descriptionData.fee_type}</p>
                )}
                
                {order.zarinpal_authority && (
                  <p><strong>Authority:</strong> <code className="authority-code">{order.zarinpal_authority}</code></p>
                )}

                {descriptionData.card_hash && (
                  <details className="card-hash-details">
                    <summary>🔐 هش کارت</summary>
                    <code className="hash-code">{descriptionData.card_hash}</code>
                  </details>
                )} */}
              </div>
            </div>
          )}

          {/* اطلاعات عمومی */}
          <div className="details-section">
            <h4 className="details-title">📋 اطلاعات عمومی</h4>
            <div className="details-grid">
              <p><strong>شناسه سفارش:</strong> {order.id}</p>
              <p><strong>تاریخ ثبت:</strong> <DateDisplay isoDate={order.created_at} /></p>
              {order.updated_at && order.updated_at !== order.created_at && (
                <p><strong>آخرین بروزرسانی:</strong> <DateDisplay isoDate={order.updated_at} /></p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// کامپوننت اصلی
const UserOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [csrfToken, setCsrfToken] = useState("");

  // دریافت CSRF Token
  useEffect(() => {
    fetch(`${BASE_URL}/csrf-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setCsrfToken(data.csrfToken))
      .catch(() => setError("خطا در دریافت توکن امنیتی"));
  }, []);

  // دریافت لیست سفارشات
  useEffect(() => {
    if (!csrfToken) return;

    setLoading(true);
    fetch(`${BASE_URL}/orders/user/${user.id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "CSRF-Token": csrfToken,
      },
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) throw new Error("خطا در دریافت سفارشات");
        return res.json();
      })
      .then((data) => {
        setOrders(data.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "خطا در دریافت اطلاعات");
        setLoading(false);
      });
  }, [csrfToken, user.id]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>در حال بارگذاری سفارشات...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">❌ {error}</p>
        <Link to="/dashboard" className="btn-back">
          🏠 بازگشت به داشبورد
        </Link>
      </div>
    );
  }

  return (
    <div className="user-orders-container">
      {/* هدر صفحه */}
      <div className="page-header">
        <h1 className="page-title">🛒 سفارشات من</h1>
        <p className="page-subtitle">مشاهده و پیگیری تمامی سفارشات ثبت‌شده</p>
      </div>

      {/* دکمه بازگشت */}
      <div className="action-bar">
        <Link to="/dashboard" className="btn-back">
          ← بازگشت به داشبورد
        </Link>
      </div>

      {/* لیست سفارشات */}
      {orders.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>هنوز سفارشی ثبت نکرده‌اید!</h3>
          <p>برای مشاهده دوره‌ها به بخش دوره‌ها بروید</p>
          <Link to="/dashboard/courses" className="btn-primary">
            🔍 مشاهده دوره‌ها
          </Link>
        </div>
      ) : (
        <div className="orders-grid">
          {orders.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}
    </div>
  );
};

export default UserOrders;
